import { useEffect, useMemo, useReducer, useRef } from "react";

/**
 * Enhanced useReducer hook with optional logging for debugging.
 *
 * This hook wraps React's useReducer and adds optional logging capabilities
 * that only run in development mode when enabled. The logging is optimized
 * to have zero performance impact in production builds.
 *
 * @param reducer - The reducer function
 * @param initialState - The initial state value
 * @param initializer - Optional function to initialize state (same as useReducer)
 * @param enableLogging - Whether to enable logging (defaults to false)
 * @returns A tuple containing the state and dispatch function
 *
 * @example
 * ```tsx
 * const [state, dispatch] = useReducerWithLogger(
 *   myReducer,
 *   initialState,
 *   undefined, // or initializer function
 *   true // enable logging in dev mode
 * );
 * ```
 */
export function useReducerWithLogger<TState, TAction extends object>(
	reducer: (state: TState, action: TAction) => TState,
	initialState: TState,
	initializer?: (initialState: TState) => TState,
	enableLogging: boolean = false,
) {
	const [state, dispatch] = useReducer(
		reducer,
		initialState,
		initializer as (initialState: TState) => TState,
	);
	// Initialize prevState with the actual initial state (after initializer if provided)
	const prevState = useRef<TState>(state);

	const dispatchWithLogger = useMemo(() => {
		return function (action: TAction) {
			if (enableLogging && import.meta.env.DEV && "type" in action) {
				console.groupCollapsed("Action Type:", action.type);
				console.log("Action:", action);
			}

			const result = dispatch(action);
			if (enableLogging && import.meta.env.DEV && "type" in action) {
				console.groupEnd();
			}
			return result;
		};
	}, [dispatch, enableLogging]);

	useEffect(() => {
		if (enableLogging && import.meta.env.DEV && state !== prevState.current) {
			console.groupCollapsed("State Change");
			console.log("Prev state:", prevState.current);
			console.log("Next state:", state);
			console.groupEnd();
		}
		prevState.current = state;
	}, [state, enableLogging]);

	return [state, dispatchWithLogger] as const;
}
